import React from 'react';
import { Box, Circle } from '@chakra-ui/react';
import { LuContact, LuMail } from 'react-icons/lu';
import { getInitials } from '@/utils/userDisplayUtils';
import DashedCircle from '@/components/settings/components/access/DashedCircle';
import { Tooltip } from '@/components/ui/tooltip';

interface PersonAvatarProps {
	name?: string | null;
	email?: string;
	showContactBadge?: boolean;
	size?: string;
	fontSize?: string;
}

/**
 * Whether a person's circle takes a colour of their own, hashed from their name. One switch for
 * every avatar, contacts and accounts alike: turning it on for one kind and not the other would
 * make the same person read as two. Off while profile circles use one neutral treatment.
 */
const COLORED_AVATARS = false;

const AVATAR_COLORS = [
	{ bg: 'gray.600', color: 'white' },
	{ bg: 'blue.600', color: 'white' },
	{ bg: 'cyan.600', color: 'white' },
	{ bg: 'purple.600', color: 'white' },
	{ bg: 'teal.600', color: 'white' },
	{ bg: 'green.600', color: 'white' },
	{ bg: 'orange.600', color: 'white' },
	{ bg: 'yellow.600', color: 'white' },
	{ bg: 'pink.600', color: 'white' },
] as const;

const getAvatarColor = (value?: string | null) => {
	const input = value?.toLowerCase() ?? '';
	const hash = [...input].reduce((sum, char) => sum + char.charCodeAt(0), 0);
	return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

/** Initials circle for named users; dashed mail circle for email-only users. */
const PersonAvatar: React.FC<PersonAvatarProps> = ({
	name,
	email,
	showContactBadge,
	size = '32px',
	fontSize = 'xs',
}) => {
	const palette = COLORED_AVATARS ? getAvatarColor(name) : { bg: 'brand.subtle', color: 'brand.fg' };
	const circle = name ? (
		<Circle
			size={size}
			bg={palette.bg}
			color={palette.color}
			fontSize={fontSize}
			fontWeight="semibold"
			flexShrink={0}
		>
			{getInitials(name, email)}
		</Circle>
	) : (
		<DashedCircle icon={<LuMail size={14} />} size={size} />
	);

	if (!showContactBadge) return circle;

	return (
		<Box position="relative" flexShrink={0}>
			{circle}
			<Tooltip content="Saved in services">
				<Box
					position="absolute"
					bottom="-4px"
					right="-4px"
					w="16px"
					h="16px"
					borderRadius="full"
					bg="white"
					border="1px solid white"
					display="flex"
					alignItems="center"
					justifyContent="center"
				>
					<LuContact size={12} color="brand.solid" />
				</Box>
			</Tooltip>
		</Box>
	);
};

export default PersonAvatar;
